import { useEffect } from "react"
import { queryOptions, useQuery } from "@tanstack/react-query"

import { apiUrl, axiosInstance } from "@/lib/api-client"
import { API_ROUTES } from "@/lib/api-routes"

/**
 * The public face of the deployment. Deliberately a smaller record than
 * `CompanyRead` — no `support_email` (a harvesting target), no legal name.
 */
export type PublicBranding = {
  name: string
  /** Hex: `#rgb`, `#rrggbb` or `#rrggbbaa`. */
  primary_color: string | null
  support_url: string | null
  /** Square mark — sidebar, avatar slots, favicon, collapsed nav. */
  has_logo: boolean
  /** Wide name-and-mark — login, banners, expanded sidebar header. */
  has_wordmark: boolean
  /** Ready-made URLs (already versioned); null when not uploaded. */
  logo_url: string | null
  wordmark_url: string | null
  support_phone: string | null
  /**
   * Changes whenever the logo or wordmark is replaced — pass it as the
   * asset URLs' version so a new upload isn't served from the old cache.
   */
  asset_version: string | null
}

export const BRANDING_QUERY_KEY = ["branding"] as const

/** What the shell renders before (or instead of) a real answer. */
const NEUTRAL: PublicBranding = {
  name: "",
  primary_color: null,
  support_url: null,
  has_logo: false,
  has_wordmark: false,
  logo_url: null,
  wordmark_url: null,
  support_phone: null,
  asset_version: null,
}

/**
 * Fetched once at boot, BEFORE auth resolves — it's the first request, not
 * something that waits for a session.
 *
 * A failure here must never block signing in, so the query resolves to a
 * neutral record instead of throwing. `retry: false` keeps a missing
 * endpoint from delaying first paint.
 */
export function brandingQueryOptions() {
  return queryOptions({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: async (): Promise<PublicBranding> => {
      try {
        const res = await axiosInstance.get<PublicBranding>(
          API_ROUTES.BRANDING.GET
        )
        return res.data
      } catch {
        // Unreachable / not deployed → run unbranded rather than break
        // the login screen.
        return NEUTRAL
      }
    },
    retry: false,
    // Changes when an admin edits Company settings in the ADMIN portal —
    // a different app, so nothing here can invalidate this key. Re-check
    // every minute and whenever the tab regains focus, so a new brand
    // colour or logo reaches customers without a reload. (Tiny, public,
    // cached endpoint.)
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    gcTime: 60 * 60_000,
  })
}

export function useBrandingQuery() {
  return useQuery(brandingQueryOptions())
}

/**
 * Square mark. Only call when `has_logo` — otherwise a guaranteed 404.
 *
 * The URL never changes, so a replacement keeps serving the cached image;
 * callers pass `asset_version` to bust it.
 */
export function brandingLogoUrl(version?: string | number | null): string {
  const base = apiUrl(API_ROUTES.BRANDING.LOGO)
  return version ? `${base}?v=${version}` : base
}

/**
 * Wide name-and-mark. Only call when `has_wordmark`.
 *
 * NOT interchangeable with the logo: letterboxing a wordmark into a
 * square slot, or cropping a square mark into a banner, always looks
 * wrong. Fall back to text instead of substituting the other asset.
 */
export function brandingWordmarkUrl(version?: string | number | null): string {
  const base = apiUrl(API_ROUTES.BRANDING.WORDMARK)
  return version ? `${base}?v=${version}` : base
}

/** Relative luminance (WCAG) — decides black vs white text on the accent. */
function readableForeground(hex: string): string {
  const h = hex.replace("#", "")
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h.slice(0, 6)
  const [r, g, b] = [0, 2, 4].map((i) => {
    const v = parseInt(full.slice(i, i + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 0.5 ? "oklch(0.145 0 0)" : "oklch(0.985 0 0)"
}

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i

/**
 * Applies the company accent as the design's `--signal` on `<html>`, so
 * primary buttons and brand moments pick it up without any component
 * knowing branding exists.
 *
 * `--on-signal` is recomputed from the accent's luminance — without that,
 * a company choosing a pale accent gets white-on-white button labels.
 */
export function useApplyBrandTheme(color: string | null | undefined) {
  useEffect(() => {
    const root = document.documentElement
    if (!color || !HEX.test(color)) {
      root.style.removeProperty("--signal")
      root.style.removeProperty("--on-signal")
      return
    }
    root.style.setProperty("--signal", color)
    root.style.setProperty("--on-signal", readableForeground(color))
    return () => {
      root.style.removeProperty("--signal")
      root.style.removeProperty("--on-signal")
    }
  }, [color])
}

/**
 * Points the browser tab icon at the company logo.
 *
 * Uses the LOGO rather than the wordmark — a favicon is a small square,
 * which is the shape the logo exists for; a wordmark scaled into 32px is
 * unreadable mush.
 *
 * Restores whatever `index.html` shipped on cleanup, so an unbranded
 * deployment (or one that removes its logo) falls back rather than
 * keeping a stale href pointing at a 404.
 */
export function useFavicon(href: string | null) {
  useEffect(() => {
    if (!href) return

    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    let created = false
    if (!link) {
      link = document.createElement("link")
      link.rel = "icon"
      document.head.appendChild(link)
      created = true
    }

    const original = link.getAttribute("href")
    // `index.html` ships `type="image/svg+xml"` for the bundled favicon,
    // but an uploaded logo is PNG/JPEG/WebP — SVG is rejected server-side
    // because it can carry script. Leaving that attribute behind declares
    // a MIME type the bytes don't match, which some browsers refuse to
    // render. Drop it and let the response's Content-Type speak.
    const originalType = link.getAttribute("type")
    link.removeAttribute("type")
    link.setAttribute("href", href)

    return () => {
      if (created) {
        link?.remove()
        return
      }
      if (original) link?.setAttribute("href", original)
      if (originalType) link?.setAttribute("type", originalType)
    }
  }, [href])
}

/** Keeps the tab title in sync — `{company} Admin`, or just `Admin`. */
export function useDocumentTitle(name: string | undefined, suffix: string) {
  useEffect(() => {
    document.title = name ? `${name} ${suffix}` : suffix
  }, [name, suffix])
}
